import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wrench, Search, Users, Calendar, Eye, Download, X } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const ToutesFormations = () => {
  const [interventions, setInterventions] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterFormateur, setFilterFormateur] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('📥 [ToutesFormations] Chargement des données...');
      
      // ✅ REQUÊTE ULTRA-SIMPLIFIÉE : Juste les interventions, sans JOIN
      console.log('🔍 Step 1: Chargement interventions...');
      const { data: interventionsData, error: interventionsError } = await supabase
        .from('interventions')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('📊 Résultat interventions:', {
        success: !interventionsError,
        count: interventionsData?.length || 0,
        error: interventionsError
      });

      if (interventionsError) {
        console.error('❌ Erreur interventions:', interventionsError);
        console.error('   Code:', interventionsError.code);
        console.error('   Message:', interventionsError.message);
        console.error('   Hint:', interventionsError.hint);
        console.error('   Details:', interventionsError.details);
      } else {
        console.log('✅ Interventions chargées:', interventionsData?.length || 0);
        if (interventionsData && interventionsData.length > 0) {
          console.log('📋 Première intervention:', interventionsData[0]);
          console.log('   Colonnes disponibles:', Object.keys(interventionsData[0]));
        } else {
          console.log('⚠️ Aucune intervention trouvée');
        }
      }
      
      // Charger les formateurs
      console.log('🔍 Step 2: Chargement formateurs...');
      const { data: formateursData, error: formateursError } = await supabase
        .from('formateurs')
        .select('id, nom, prenom')
        .order('nom', { ascending: true });

      if (formateursError) {
        console.error('❌ Erreur formateurs:', formateursError);
      } else {
        console.log('✅ Formateurs chargés:', formateursData?.length || 0);
      }
      
      // Association manuelle interventions ↔ formateurs
      let interventionsWithFormateurs = interventionsData || [];
      if (interventionsData && formateursData && interventionsData.length > 0) {
        console.log('🔗 Step 3: Association interventions-formateurs...');
        interventionsWithFormateurs = interventionsData.map(intervention => {
          const formateur = formateursData.find(f => {
            // Gérer différents types de trainer_id
            if (intervention.trainer_id === null) return false;
            return f.id === intervention.trainer_id || 
                   f.id.toString() === intervention.trainer_id?.toString();
          });
          
          return {
            ...intervention,
            formateur: formateur || null
          };
        });
        
        const nbAvecFormateur = interventionsWithFormateurs.filter(i => i.formateur).length;
        console.log(`✅ ${nbAvecFormateur}/${interventionsWithFormateurs.length} interventions associées à un formateur`);
      }
      
      setInterventions(interventionsWithFormateurs);
      setFormateurs(formateursData || []);
      
      console.log('✅ [ToutesFormations] Chargement terminé');
    } catch (error) {
      console.error('❌ Erreur fatale chargement données:', error);
      console.error('   Type:', error.name);
      console.error('   Message:', error.message);
      console.error('   Stack:', error.stack);
    } finally {
      setLoading(false);
    }
  };

  const filteredInterventions = interventions.filter(intervention => {
    const matchSearch = !searchTerm || 
      intervention.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.immatriculation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.work_description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchType = filterType === 'all' || intervention.intervention_type === filterType;
    
    const matchFormateur = filterFormateur === 'all' || 
      intervention.trainer_id?.toString() === filterFormateur;

    return matchSearch && matchType && matchFormateur;
  });

  const stats = {
    total: interventions.length,
    mecanique: interventions.filter(i => i.intervention_type === 'mechanical').length,
    carrosserie: interventions.filter(i => i.intervention_type === 'bodywork').length,
    formateurs: formateurs.length
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-600 p-3 rounded-lg">
              <Wrench className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Toutes les Interventions Pédagogiques</h1>
              <p className="text-gray-300">Vue globale de toutes les interventions réalisées</p>
            </div>
          </div>

          {/* Filtres */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Type d'intervention */}
              <div className="relative">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="all">Tous les types</option>
                  <option value="mechanical">Mécanique</option>
                  <option value="bodywork">Carrosserie</option>
                </select>
              </div>

              {/* Formateur */}
              <div className="relative">
                <select
                  value={filterFormateur}
                  onChange={(e) => setFilterFormateur(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="all">Tous les formateurs</option>
                  {formateurs.map(formateur => (
                    <option key={formateur.id} value={formateur.id.toString()}>
                      {formateur.prenom} {formateur.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Stats rapides */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-700">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-400">{stats.total}</p>
                <p className="text-xs text-gray-400">Total</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">{stats.mecanique}</p>
                <p className="text-xs text-gray-400">Mécanique</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-400">{stats.carrosserie}</p>
                <p className="text-xs text-gray-400">Carrosserie</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-400">{stats.formateurs}</p>
                <p className="text-xs text-gray-400">Formateurs</p>
              </div>
            </div>

            {/* Filtres actifs */}
            {(searchTerm || filterType !== 'all' || filterFormateur !== 'all') && (
              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-700">
                <span className="text-sm text-gray-400">Filtres actifs:</span>
                {searchTerm && (
                  <span className="bg-blue-600/20 text-blue-400 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    Recherche: "{searchTerm}"
                    <button onClick={() => setSearchTerm('')} className="hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filterType !== 'all' && (
                  <span className="bg-green-600/20 text-green-400 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {filterType === 'mechanical' ? 'Mécanique' : 'Carrosserie'}
                    <button onClick={() => setFilterType('all')} className="hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filterFormateur !== 'all' && (
                  <span className="bg-purple-600/20 text-purple-400 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {formateurs.find(f => f.id.toString() === filterFormateur)?.prenom} {formateurs.find(f => f.id.toString() === filterFormateur)?.nom}
                    <button onClick={() => setFilterFormateur('all')} className="hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Liste des interventions */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-400">Chargement...</p>
          </div>
        ) : filteredInterventions.length === 0 ? (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-12 text-center">
            <Wrench className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Aucune intervention trouvée
            </h3>
            <p className="text-gray-400">
              {searchTerm || filterType !== 'all' || filterFormateur !== 'all'
                ? 'Aucune intervention ne correspond à vos filtres' 
                : 'Aucune intervention enregistrée pour le moment'}
            </p>
            {interventions.length === 0 && (
              <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  💡 Vérifiez la console (F12) pour plus de détails
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredInterventions.map((intervention, index) => {
              const formateurNom = intervention.formateur 
                ? `${intervention.formateur.prenom} ${intervention.formateur.nom}` 
                : 'Non assigné';
              
              return (
                <motion.div
                  key={intervention.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6 hover:border-blue-500 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {intervention.immatriculation || 'N/A'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          intervention.intervention_type === 'mechanical' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-purple-600 text-white'
                        }`}>
                          {intervention.intervention_type === 'mechanical' ? 'Mécanique' : 'Carrosserie'}
                        </span>
                        {intervention.status === 'completed' && (
                          <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-semibold">
                            ✓ Complété
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-semibold text-white mb-2">
                        {intervention.client_name || 'Client non renseigné'}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-400">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{formateurNom}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {intervention.created_at 
                              ? new Date(intervention.created_at).toLocaleDateString('fr-FR')
                              : 'Date inconnue'}
                          </span>
                        </div>
                        {intervention.work_description && (
                          <div className="col-span-full text-gray-300">
                            {intervention.work_description.substring(0, 100)}
                            {intervention.work_description.length > 100 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToutesFormations;